/**
 * StorefrontSettings — operator-side controls for the public `/shop` surface
 * (publish toggle, store name, minimum price, show-listed toggle, hero
 * banner upload). Lives in its own file because settings.tsx was getting
 * unwieldy; pure refactor extracted as TNK-106 phase 1, no UX change.
 *
 * Mounted by routes/settings.tsx; consumed by manager+ users via the
 * RequireRole wrapper there.
 */

import { useEffect, useState } from "react";
import { Store } from "lucide-react";

import { CardImageUploader } from "@/components/CardImage";
import { FormField } from "@/components/FormField";
import { Ticker } from "@/components/shop/Ticker";
import type { StoreImages } from "@/context/AppContext";
import { deleteImage } from "@/features/imageUpload";
import type { StorefrontGate, TickerConfig } from "@/features/storefront";
import { apiFetch } from "@/lib/apiClient";
import { StorefrontGateEditor } from "@/components/settings/StorefrontGateEditor";

interface StoreProfile {
  storeName: string;
  storefrontEnabled: boolean;
  storefrontMinPrice: number;
  showListed: boolean;
  logoRef: string | null;
  watermarkRef: string | null;
  heroRef: string | null;
  // TNK-204 — hero band tuning; null = app defaults (280px / 50% / cover).
  heroHeight: number | null;
  heroOpacity: number | null;
  heroFit: string | null;
  // TNK-205 — /shop landing title block position; null = center / top.
  landingTitleAlign: string | null;
  landingTitleValign: string | null;
  // TNK-206 — show the hero banner on the /shop landing.
  landingShowHero: boolean | null;
  // TNK-208 — storefront ticker config.
  tickerConfig: TickerConfig | null;
  // TNK-212 — storefront eligibility gate (which singles appear).
  storefrontGate: StorefrontGate | null;
  // TNK-177 — the operator's private sign-in path (`/<slug>`). Read/written via
  // this authed profile only; never exposed publicly.
  operatorDoorSlug: string;
}

const DEFAULT_STORE_PROFILE: StoreProfile = {
  storeName: "",
  storefrontEnabled: false,
  storefrontMinPrice: 0.24,
  showListed: false,
  logoRef: null,
  watermarkRef: null,
  heroRef: null,
  heroHeight: null,
  heroOpacity: null,
  heroFit: null,
  landingTitleAlign: null,
  landingTitleValign: null,
  landingShowHero: null,
  tickerConfig: null,
  storefrontGate: null,
  operatorDoorSlug: "admin",
};

type LoadState = "loading" | "ready" | "disabled" | "unauth" | "error";

export function StorefrontSettings({
  storeImages,
}: {
  storeImages: StoreImages;
}) {
  const [load, setLoad] = useState<LoadState>("loading");
  const [form, setForm] = useState<StoreProfile>(DEFAULT_STORE_PROFILE);
  const [minPrice, setMinPrice] = useState("0.24");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch("/api/store/profile/me");
        if (cancelled) return;
        if (res.status === 404) {
          setLoad("disabled");
          return;
        }
        if (res.status === 401) {
          setLoad("unauth");
          return;
        }
        if (!res.ok) {
          setLoad("error");
          return;
        }
        const profile = (await res.json()) as StoreProfile | null;
        if (cancelled) return;
        const next = profile
          ? { ...DEFAULT_STORE_PROFILE, ...profile }
          : DEFAULT_STORE_PROFILE;
        setForm(next);
        setMinPrice(String(next.storefrontMinPrice));
        setLoad("ready");
      } catch {
        if (!cancelled) setLoad("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const parsedMin = parseFloat(minPrice);
      const res = await apiFetch("/api/store/profile/me", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storeName: form.storeName,
          storefrontEnabled: form.storefrontEnabled,
          storefrontMinPrice:
            !isNaN(parsedMin) && parsedMin >= 0 ? parsedMin : 0.24,
          showListed: form.showListed,
          // Mirror the seller's branding so the public storefront can render it.
          logoRef: storeImages.logo ?? null,
          watermarkRef: storeImages.watermark ?? null,
          heroRef: form.heroRef,
          heroHeight: form.heroHeight,
          heroOpacity: form.heroOpacity,
          heroFit: form.heroFit,
          landingTitleAlign: form.landingTitleAlign,
          landingTitleValign: form.landingTitleValign,
          landingShowHero: form.landingShowHero ?? false,
          tickerConfig: form.tickerConfig,
          storefrontGate: form.storefrontGate,
          operatorDoorSlug: form.operatorDoorSlug,
        }),
      });
      if (!res.ok) {
        // Surface the server's validation message (e.g. a bad/reserved door slug)
        // rather than a generic failure.
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setNotice(body.error ?? "Could not save — please try again.");
        return;
      }
      setNotice("Saved.");
    } catch {
      setNotice("Network error — please try again.");
    } finally {
      setSaving(false);
    }
  };

  // TNK-208 — sample items so the operator can preview the ticker in settings
  // (the live counts/names aren't available here; these are representative).
  const tc = form.tickerConfig ?? {};
  const previewItems: string[] = [];
  if (tc.shipped) previewItems.push("✓ 142 cards shipped");
  if (tc.available) previewItems.push("4,772 cards available");
  if (tc.pricesFrom) previewItems.push("Singles from $0.24");
  if (tc.justSold) previewItems.push("Just sold: Charizard ex");
  if (tc.recentlyAdded) previewItems.push("Just added: Pikachu V");
  if (tc.games) previewItems.push("Pokémon & Magic");
  (tc.custom ?? []).forEach((m) => {
    const t = m.trim();
    if (t) previewItems.push(t);
  });

  return (
    <section className="space-y-3 rounded-md border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <Store className="h-4 w-4 text-muted-foreground" />
        <div>
          <h2 className="text-sm font-semibold text-foreground">Storefront</h2>
          <p className="text-[11px] text-muted-foreground">
            Publish a public page at <code>/shop</code> where customers can
            browse your eligible cards and reserve them. Uses your store
            branding above.
          </p>
        </div>
      </div>

      {load === "loading" && (
        <p className="text-[11px] text-muted-foreground">Loading…</p>
      )}
      {load === "disabled" && (
        <p className="text-[11px] text-muted-foreground">
          The storefront feature is turned off on the server. Set{" "}
          <code>STOREFRONT_ENABLED=true</code> in the server <code>.env</code>{" "}
          and restart the API to configure it.
        </p>
      )}
      {load === "unauth" && (
        <p className="text-[11px] text-muted-foreground">
          Sign in with eBay to configure your storefront.
        </p>
      )}
      {load === "error" && (
        <p className="text-[11px] text-destructive">
          Couldn’t load storefront settings.
        </p>
      )}

      {load === "ready" && (
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={form.storefrontEnabled}
              onChange={(e) =>
                setForm((f) => ({ ...f, storefrontEnabled: e.target.checked }))
              }
              className="h-4 w-4"
            />
            Publish storefront (make <code>/shop</code> live)
          </label>

          <FormField
            label="Store name"
            value={form.storeName}
            onChangeText={(v) => setForm((f) => ({ ...f, storeName: v }))}
            placeholder="e.g. ThatNerd's Cards"
          />

          <div className="max-w-[200px]">
            <FormField
              label="Storefront minimum price"
              value={minPrice}
              onChangeText={setMinPrice}
              inputMode="decimal"
              suffix="USD"
              placeholder="0.24"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={form.showListed}
              onChange={(e) =>
                setForm((f) => ({ ...f, showListed: e.target.checked }))
              }
              className="h-4 w-4"
            />
            Also show cards already listed on eBay
          </label>

          <div className="border-t border-border pt-3">
            <StorefrontGateEditor
              value={form.storefrontGate}
              onChange={(g) => setForm((f) => ({ ...f, storefrontGate: g }))}
              storeMinPrice={
                Number.isFinite(parseFloat(minPrice))
                  ? parseFloat(minPrice)
                  : undefined
              }
            />
          </div>

          <div className="max-w-md">
            <CardImageUploader
              label="Hero banner"
              imageRef={form.heroRef ?? undefined}
              onChange={(newRef) => {
                if (form.heroRef && form.heroRef !== newRef) {
                  void deleteImage(form.heroRef);
                }
                setForm((f) => ({ ...f, heroRef: newRef ?? null }));
              }}
              fit="free"
              captureFromCamera={false}
              hint="Shows on /shop and /shop/inventory at the height + opacity set below (image fills the band). Wide aspects (3:1–5:1, e.g. 1600×400) read best. Leave empty to use the default gradient."
            />

            {/* TNK-204 — operator-tunable band height + image opacity. */}
            <div className="mt-3 space-y-3">
              <label className="block space-y-1">
                <span className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                  <span>Hero height</span>
                  <span className="text-foreground">
                    {form.heroHeight ?? 280}px
                  </span>
                </span>
                <input
                  type="range"
                  min={120}
                  max={600}
                  step={10}
                  value={form.heroHeight ?? 280}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      heroHeight: Number(e.target.value),
                    }))
                  }
                  className="w-full"
                />
              </label>
              <label className="block space-y-1">
                <span className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                  <span>Banner opacity</span>
                  <span className="text-foreground">
                    {form.heroOpacity ?? 50}%
                  </span>
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={form.heroOpacity ?? 50}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      heroOpacity: Number(e.target.value),
                    }))
                  }
                  className="w-full"
                />
              </label>
              <label className="block space-y-1">
                <span className="text-xs font-medium text-muted-foreground">
                  Image fit
                </span>
                <select
                  value={form.heroFit ?? "cover"}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, heroFit: e.target.value }))
                  }
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                >
                  <option value="cover">Fill — crop to fill the band</option>
                  <option value="contain">Fit — show the whole image</option>
                  <option value="fill">Stretch — fill, may distort</option>
                </select>
              </label>
              <p className="text-[11px] text-muted-foreground">
                Save, then refresh your storefront to see the banner update.
              </p>
            </div>
          </div>

          {/* TNK-205/206 — public /shop landing: hero toggle + title position. */}
          <div className="max-w-md space-y-2 border-t border-border pt-3">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={!!form.landingShowHero}
                onChange={(e) =>
                  setForm((f) => ({ ...f, landingShowHero: e.target.checked }))
                }
                className="h-4 w-4"
              />
              Show the hero banner on the landing page
            </label>
            <p className="text-xs font-medium text-foreground">
              Landing title position
            </p>
            <div className="flex gap-2">
              <label className="flex-1 space-y-1">
                <span className="block text-[11px] text-muted-foreground">
                  Horizontal
                </span>
                <select
                  value={form.landingTitleAlign ?? "center"}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      landingTitleAlign: e.target.value,
                    }))
                  }
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </label>
              <label className="flex-1 space-y-1">
                <span className="block text-[11px] text-muted-foreground">
                  Vertical
                </span>
                <select
                  value={form.landingTitleValign ?? "top"}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      landingTitleValign: e.target.value,
                    }))
                  }
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                >
                  <option value="top">Top</option>
                  <option value="center">Middle</option>
                  <option value="bottom">Bottom</option>
                </select>
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Positions the store name + button on your public{" "}
              <code>/shop</code> landing.
            </p>
          </div>

          {/* TNK-208 — buyer-confidence ticker shown above the /shop/inventory
              hero. Each item toggles independently; the strip rotates them. */}
          <div className="max-w-md space-y-2 border-t border-border pt-3">
            <p className="text-xs font-medium text-foreground">
              Storefront ticker
            </p>
            <p className="text-[11px] text-muted-foreground">
              A rotating strip above the inventory hero — pick which buyer
              signals to show.
            </p>

            {/* Live preview (sample data) so changes are visible here. */}
            <div className="space-y-1">
              <span className="text-[11px] text-muted-foreground">Preview</span>
              {previewItems.length > 0 ? (
                <Ticker
                  items={previewItems}
                  motion={tc.motion ?? "fade"}
                  speed={tc.speed ?? "medium"}
                  gap={tc.gap ?? 48}
                  flush={false}
                />
              ) : (
                <p className="rounded-md border border-dashed border-border px-3 py-3 text-center text-[11px] text-muted-foreground">
                  Enable items below to preview the ticker.
                </p>
              )}
            </div>
            {(
              [
                ["shipped", "Cards shipped"],
                ["available", "Cards in stock"],
                ["pricesFrom", "Prices from $X"],
                ["justSold", "Just sold (latest card)"],
                ["recentlyAdded", "Recently added (latest card)"],
                ["games", "Games carried"],
              ] as const
            ).map(([key, label]) => (
              <label
                key={key}
                className="flex items-center gap-2 text-sm text-foreground"
              >
                <input
                  type="checkbox"
                  checked={!!form.tickerConfig?.[key]}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      tickerConfig: {
                        ...(f.tickerConfig ?? {}),
                        [key]: e.target.checked,
                      },
                    }))
                  }
                  className="h-4 w-4"
                />
                {label}
              </label>
            ))}
            <label className="block space-y-1">
              <span className="text-[11px] text-muted-foreground">
                Custom lines (one per line, up to 5)
              </span>
              <textarea
                rows={3}
                value={(form.tickerConfig?.custom ?? []).join("\n")}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    tickerConfig: {
                      ...(f.tickerConfig ?? {}),
                      custom: e.target.value.split("\n"),
                    },
                  }))
                }
                placeholder={"Flat $1 shipping\nReserve & we hold it 7 days"}
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] text-muted-foreground">Motion</span>
              <select
                value={form.tickerConfig?.motion ?? "fade"}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    tickerConfig: {
                      ...(f.tickerConfig ?? {}),
                      motion: e.target.value as "fade" | "marquee" | "static",
                    },
                  }))
                }
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              >
                <option value="fade">Fade rotate</option>
                <option value="marquee">Scrolling marquee</option>
                <option value="static">Static (no motion)</option>
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] text-muted-foreground">
                Speed (marquee)
              </span>
              <select
                value={form.tickerConfig?.speed ?? "medium"}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    tickerConfig: {
                      ...(f.tickerConfig ?? {}),
                      speed: e.target.value as "slow" | "medium" | "fast",
                    },
                  }))
                }
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              >
                <option value="slow">Slow</option>
                <option value="medium">Medium</option>
                <option value="fast">Fast</option>
              </select>
            </label>
            <label className="block space-y-1">
              <span className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>Space between items</span>
                <span className="text-foreground">
                  {form.tickerConfig?.gap ?? 48}px
                </span>
              </span>
              <input
                type="range"
                min={8}
                max={96}
                step={4}
                value={form.tickerConfig?.gap ?? 48}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    tickerConfig: {
                      ...(f.tickerConfig ?? {}),
                      gap: Number(e.target.value),
                    },
                  }))
                }
                className="w-full"
              />
            </label>
          </div>

          {/* TNK-177 — operator "door". The login is reachable only at this
              path; everything else sends signed-out visitors to the store. No
              link points here, so it must be bookmarked. */}
          <div className="space-y-1 border-t border-border pt-3">
            <FormField
              label="Operator login URL (door)"
              value={form.operatorDoorSlug}
              onChangeText={(v) =>
                setForm((f) => ({
                  ...f,
                  operatorDoorSlug: v.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                }))
              }
              placeholder="admin"
            />
            <p className="text-[11px] text-muted-foreground">
              Your private sign-in address:{" "}
              <code>
                {window.location.origin}/{form.operatorDoorSlug || "your-door"}
              </code>
              . Lowercase letters, numbers, hyphens (3–41 chars). Change it from
              the default <code>admin</code> so it isn&rsquo;t a guessable
              address — nothing links to it, so bookmark it. (Forgotten slugs
              can be reset in the database.)
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save storefront"}
            </button>
            {notice && (
              <span className="text-[11px] text-muted-foreground">
                {notice}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
